import { useState } from "react";
import { Sparkles, Pencil, Check, X } from "lucide-react";
import { ROLE_LABEL } from "./visitorPatterns";

/**
 * Personalized banner that sits above the chat transcript once Jerry knows
 * the visitor's name. Supports inline editing of name + role and a "Not you?"
 * shortcut for forgetting.
 */
export default function VisitorBanner({ name, role, onUpdate, onForget }) {
    const [editing, setEditing] = useState(false);
    const [draftName, setDraftName] = useState(name || "");
    const [draftRole, setDraftRole] = useState(role || "");

    const openEdit = () => {
        setDraftName(name || "");
        setDraftRole(role || "");
        setEditing(true);
    };

    const save = () => {
        onUpdate(draftName, draftRole);
        setEditing(false);
    };

    return (
        <div
            className="px-5 py-2.5 bg-gradient-to-r from-[#EFEBE0] to-[#E8EFE8] border-b border-[#E5DFD3]"
            data-testid="visitor-banner"
        >
            {!editing ? (
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-[12.5px] text-[#1A2230] min-w-0">
                        <Sparkles className="h-3.5 w-3.5 text-[#C8A24A] shrink-0" />
                        <span className="truncate">
                            Hi <span className="font-semibold">{name}</span>
                            {role && (
                                <span className="text-[#6F6B63]"> · {ROLE_LABEL[role] || role}</span>
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            onClick={openEdit}
                            className="text-[#6F6B63] hover:text-[#0E2A47] transition-colors"
                            data-testid="visitor-banner-edit"
                            title="Edit name or role"
                            aria-label="Edit"
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                            onClick={onForget}
                            className="text-[11.5px] text-[#6F6B63] hover:text-[#0E2A47] underline-offset-2 hover:underline"
                            data-testid="visitor-banner-forget"
                            title="Forget my details"
                        >
                            Not you?
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-2" data-testid="visitor-banner-edit-form">
                    <input
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        placeholder="First name"
                        className="flex-1 min-w-0 rounded-full bg-white border border-[#E5DFD3] px-3 py-1.5 text-[12.5px] focus:outline-none focus:border-[#0E2A47]"
                        data-testid="visitor-banner-edit-name"
                        autoFocus
                    />
                    <select
                        value={draftRole}
                        onChange={(e) => setDraftRole(e.target.value)}
                        className="rounded-full bg-white border border-[#E5DFD3] px-3 py-1.5 text-[12px] focus:outline-none focus:border-[#0E2A47] max-w-[140px]"
                        data-testid="visitor-banner-edit-role"
                    >
                        <option value="">No role</option>
                        {Object.entries(ROLE_LABEL).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <button
                        onClick={save}
                        className="rounded-full bg-[#0E2A47] text-white h-7 w-7 grid place-items-center hover:bg-[#08182B]"
                        data-testid="visitor-banner-save"
                        title="Save"
                    >
                        <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={() => setEditing(false)}
                        className="rounded-full border border-[#E5DFD3] text-[#6F6B63] h-7 w-7 grid place-items-center hover:border-[#0E2A47]"
                        data-testid="visitor-banner-cancel"
                        title="Cancel"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
}
